import { z } from 'zod';
export const DataSourceKindSchema = z.enum([
    'postgres',
    'mysql',
    /** MariaDB — mysql2 드라이버 공용. statement timeout 등 일부 방언이 MySQL 과 달라 별도 kind. */
    'mariadb',
    'mssql',
    'oracle',
    'bigquery',
    'excel',
    /** Tableau Server 연결. listTables 등 sql 동기화 대상이 아니라 자원 (workbook/datasource) 카탈로그 진입점. */
    'tableau',
    /** Tableau 통합 산출물 (.duckdb 파일). 실제 db.* 툴이 질의하는 대상. */
    'duckdb',
]);
export const SslModeSchema = z.enum(['disable', 'require', 'verify-full']);
export const DataSourceSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    kind: DataSourceKindSchema,
    host: z.string(),
    port: z.number().int(),
    database: z.string(),
    username: z.string(),
    sslMode: SslModeSchema,
    schemaFilter: z.array(z.string()),
    /**
     * 비밀이 아닌 kind 별 접속 옵션(oracle serviceName/sid, mssql encrypt, bigquery projectId/location 등).
     * 비밀(비밀번호·서비스계정 JSON)은 passwordEncrypted 에만 있고 응답에 노출되지 않는다.
     * 편집 폼이 기존 값을 복원하는 데 사용.
     */
    extraConfig: z.record(z.string(), z.unknown()),
    description: z.string().nullable(),
    structureSyncedAt: z.string().nullable(),
    statsSyncedAt: z.string().nullable(),
    descriptionsGeneratedAt: z.string().nullable(),
    lastSyncError: z.string().nullable(),
    /** 진행 중 작업 협력적 취소 신호 (서버가 NOW() 로 마킹). */
    cancelRequestedAt: z.string().nullable(),
    createdAt: z.string(),
    tableCount: z.number().int().nonnegative(),
    columnCount: z.number().int().nonnegative(),
});
export const DataSourceListSchema = z.object({
    dataSources: z.array(DataSourceSchema),
});
/**
 * 시스템 카탈로그 스키마는 절대 노출하지 않는다 (서버에서 reject).
 * 어드민이 schemaFilter 에 직접 입력해도 차단됨.
 */
export const FORBIDDEN_SCHEMAS = [
    'pg_catalog',
    'information_schema',
    'pg_toast',
    'sys',
    'mysql',
    'performance_schema',
    'INFORMATION_SCHEMA',
    // Oracle 시스템 owner — 데이터 딕셔너리/내장 패키지 스키마. 절대 sync/질의 대상 아님.
    'SYS',
    'SYSTEM',
    'OUTLN',
    'XDB',
    'MDSYS',
    'CTXSYS',
    'DBSNMP',
    'APPQOSSYS',
    'AUDSYS',
    'GSMADMIN_INTERNAL',
];
export const CreateDataSourceRequestSchema = z.object({
    name: z.string().min(1).max(80),
    kind: DataSourceKindSchema,
    host: z.string().min(1).max(255),
    port: z.number().int().min(1).max(65535),
    database: z.string().min(1).max(128),
    username: z.string().min(1).max(128),
    // 일반 DB 비밀번호는 짧지만, BigQuery 는 서비스계정 JSON(~2.3KB) 전체를 이 필드로 받아
    // passwordEncrypted 에 암호화 저장한다. 그래서 상한을 넉넉히 둔다.
    password: z.string().max(20000),
    sslMode: SslModeSchema.default('require'),
    schemaFilter: z
        .array(z.string().min(1).max(128))
        .max(50)
        .default([])
        .refine((arr) => arr.every((s) => !FORBIDDEN_SCHEMAS.includes(s) && !s.toLowerCase().startsWith('pg_')), { message: '시스템 카탈로그 스키마는 등록할 수 없습니다.' }),
    extraConfig: z.record(z.string(), z.unknown()).default({}),
    description: z.string().max(2000).nullable().default(null),
});
export const UpdateDataSourceRequestSchema = z.object({
    name: z.string().min(1).max(80).optional(),
    host: z.string().min(1).max(255).optional(),
    port: z.number().int().min(1).max(65535).optional(),
    database: z.string().min(1).max(128).optional(),
    username: z.string().min(1).max(128).optional(),
    password: z.string().max(20000).optional(),
    sslMode: SslModeSchema.optional(),
    schemaFilter: z.array(z.string().min(1).max(128)).max(50).optional(),
    extraConfig: z.record(z.string(), z.unknown()).optional(),
    description: z.string().max(2000).nullable().optional(),
});
export const TestConnectionRequestSchema = CreateDataSourceRequestSchema;
export const TestConnectionResponseSchema = z.object({
    ok: z.boolean(),
    serverVersion: z.string().nullable(),
    error: z.string().nullable(),
    latencyMs: z.number().int().nonnegative(),
});
/**
 * description 이 누구에 의해 채워졌는지.
 * - null: 아직 없음 (비동기 LLM 유추 대기 중이거나 comment·LLM 모두 실패)
 * - 'comment': 원본 DB COMMENT 를 그대로 복사. 다음 sync 에서 comment 가 바뀌면 자동 갱신.
 * - 'llm': 비동기 태스크가 LLM 으로 유추한 설명.
 * - 'manual': 관리자가 직접 입력. sync 에서 자동 덮어쓰지 않는다.
 */
export const DescriptionSourceSchema = z.enum(['manual', 'comment', 'llm']);
export const DataSourceColumnSchema = z.object({
    id: z.string().uuid(),
    columnName: z.string(),
    ordinal: z.number().int(),
    dataType: z.string(),
    isNullable: z.boolean(),
    defaultValue: z.string().nullable(),
    isPk: z.boolean(),
    fkTarget: z.string().nullable(),
    sourceComment: z.string().nullable(),
    description: z.string().nullable(),
    descriptionSource: DescriptionSourceSchema.nullable(),
    cardinality: z.number().int().nullable(),
    nullCount: z.number().int().nullable(),
    minValue: z.string().nullable(),
    maxValue: z.string().nullable(),
    sampleValues: z.array(z.unknown()).nullable(),
    statsSyncedAt: z.string().nullable(),
    removedAt: z.string().nullable(),
});
export const DataSourceTableSchema = z.object({
    id: z.string().uuid(),
    schemaName: z.string(),
    tableName: z.string(),
    isView: z.boolean(),
    estimatedRowCount: z.number().int().nullable(),
    sourceComment: z.string().nullable(),
    description: z.string().nullable(),
    descriptionSource: DescriptionSourceSchema.nullable(),
    /** 사용자별 필터에 쓰는 컬럼명. null 이면 공용 테이블. */
    userScopeColumn: z.string().nullable(),
    /** true 면 통계 sync 시 인덱스 첫 컬럼만 수집 (lite 모드). 통계 timeout 시 자동 true. */
    isBigTable: z.boolean(),
    /** 테이블 단위 통계 sync 완료 시각. null 이면 아직/재개 대기. */
    statsSyncedAt: z.string().nullable(),
    /** 마지막 통계 수집이 실패했을 때의 시각. */
    statsFailedAt: z.string().nullable(),
    /** 마지막 통계 수집 실패 메시지. */
    statsError: z.string().nullable(),
    syncedAt: z.string(),
    removedAt: z.string().nullable(),
    columnCount: z.number().int().nonnegative(),
});
export const DataSourceIndexSchema = z.object({
    id: z.string().uuid(),
    indexName: z.string(),
    /** 순서 있는 컬럼명 배열. 표현식 인덱스는 `(expr)` 형태. */
    columnNames: z.array(z.string()),
    isUnique: z.boolean(),
    isPrimary: z.boolean(),
    /** btree, hash, gin, gist, brin 등. null 이면 수집 실패. */
    indexMethod: z.string().nullable(),
    /** partial index 의 WHERE 절. */
    predicate: z.string().nullable(),
    syncedAt: z.string(),
    removedAt: z.string().nullable(),
});
export const DataSourceTableListSchema = z.object({
    tables: z.array(DataSourceTableSchema),
});
export const DataSourceTableDetailSchema = z.object({
    table: DataSourceTableSchema,
    columns: z.array(DataSourceColumnSchema),
    indexes: z.array(DataSourceIndexSchema),
});
export const UpdateTableDescriptionRequestSchema = z.object({
    description: z.string().max(4000).nullable(),
});
export const UpdateTableUserScopeRequestSchema = z.object({
    /** null 또는 빈 문자열이면 공용(필터 해제). 그 외엔 컬럼명. */
    userScopeColumn: z.string().max(200).nullable(),
});
export const UpdateColumnDescriptionRequestSchema = UpdateTableDescriptionRequestSchema;
export const SyncScopeSchema = z.enum(['structure', 'stats', 'all']);
export const SyncRequestSchema = z.object({
    scope: SyncScopeSchema.default('all'),
    tableIds: z.array(z.string().uuid()).optional(),
    /**
     * true 면 statsSyncedAt / isBigTable 등을 모두 초기화하고 처음부터 통계 수집.
     * 기본 false → 이미 statsSyncedAt 가 있는 테이블은 skip (재개 모드).
     */
    fullReset: z.boolean().optional(),
});
export const SyncResponseSchema = z.object({
    jobKeys: z.array(z.string()),
});
export const SyncCancelResponseSchema = z.object({
    ok: z.boolean(),
    cancelledCount: z.number().int().nonnegative(),
});
export const GenerateDescriptionsResponseSchema = z.object({
    jobKey: z.string(),
});
/**
 * 데이터소스 테이블 단위 즉시 미리보기 응답.
 * 1000행 / 100컬럼 / 60초 statement_timeout 상한.
 */
export const DataSourceTablePreviewResponseSchema = z.object({
    schemaName: z.string(),
    tableName: z.string(),
    columns: z.array(z.string()),
    rows: z.array(z.array(z.unknown())),
    rowCount: z.number().int().nonnegative(),
    /** 1000행 이상 — LIMIT 으로 잘렸음. */
    truncated: z.boolean(),
    /** 컬럼 100개 이상 — 처음 100개만 반환. */
    truncatedColumns: z.boolean(),
    latencyMs: z.number().int().nonnegative(),
});
/** 어떤 dialect 가 사용 가능한지 (UI select disable 용). */
export const SUPPORTED_DATA_SOURCE_KINDS = [
    'postgres',
    'mysql',
    'mariadb',
    'mssql',
    'oracle',
    'bigquery',
    'excel',
    'tableau',
    'duckdb',
];
export function isDataSourceKindSupported(kind) {
    return SUPPORTED_DATA_SOURCE_KINDS.includes(kind);
}
/** kind 별 기본 포트 (UI prefill). excel/duckdb 는 외부 접속 없으므로 0. */
export const DATA_SOURCE_DEFAULT_PORT = {
    postgres: 5432,
    mysql: 3306,
    mariadb: 3306,
    mssql: 1433,
    oracle: 1521,
    bigquery: 443,
    excel: 0,
    tableau: 443,
    duckdb: 0,
};
//# sourceMappingURL=data-sources.js.map